<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { useAuthStore } from "@core/stores/auth.store"
import type { BreadcrumbItem } from "@core/components/layout/header/HeaderPage.vue"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useStorage } from "@vueuse/core"

// ================================
// PROPS
// ================================
type Props = {
    /**
     * Fil d’Ariane au-dessus du titre (ex. Accueil → Tableau de bord).
     */
    breadcrumb?: BreadcrumbItem[]
    /**
     * Affiche le message d’aide (masqué pendant le chargement initial du tableau de bord).
     */
    showHelperMessage?: boolean
}

withDefaults(defineProps<Props>(), {
    breadcrumb: () => [],
    showHelperMessage: true,
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const emConsoCompareStore = useEmConsoCompareStore()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Bonjour {firstname}",
            sub_title: () => {
                const mappingRole = {
                    employe: 'que <span class="font-bold">membre</span>',
                    administrateur:
                        'qu\'<span class="font-bold">administrateur</span>',
                    employe_read_only:
                        'qu\'<span class="font-bold">utilisateur</span>',
                    owner: 'que <span class="font-bold">super admin</span>',
                } as Record<string, string>

                const role =
                    mappingRole[authStore.role ?? ""] ||
                    mappingRole["employe_read_only"]

                return `Vous êtes actuellement connecté en tant ${role} sur l'espace <span class="font-bold">${authStore.companyName}</span>`
            },
            export: "Exporter",
            export_rt: "Données Référentiel Technique",
            analyse_title: "Aperçu global sur",
            analyse_folder: "1 dossier | {total} dossiers",
            analyse_building: "1 bâtiment | {total} bâtiments",
            source: "avec 1 source | avec {total} sources",
            analyse_folder_tooltip: () => {
                return emConsoCompareStore.getComparedFoldersList
                    ?.map((folder) => `• ${folder.name}`)
                    .join("\n")
            },
            analyse_building_tooltip: () => {
                return emConsoCompareStore.getComparedBuildingsList
                    ?.map(
                        (building) =>
                            `• ${building.name ? building.name : building.address?.inline_name}`
                    )
                    .join("\n")
            },
            max_source_warning: {
                title: "Volume élevé de sources/bâtiments",
                content:
                    "Certaines combinaisons de variables, périodes et fréquences peuvent être soit incompatibles, soit indisponibles pour l'analyse en raison du volume des données. Ajustez vos paramètres si nécessaire.",
            },
            modify: "Modifier",
        },
    },
})

// ================================
// STATE
// ================================
const visible = ref(false)
const visibleModalExport = ref(false)
const visibleModalExportRt = ref(false)

const isHelperMessageVisible = useStorage("dashboard-header-message", true)

// ================================
// COMPUTED
// ================================
/**
 * Titre de l’aperçu global (dossiers / bâtiments + sources).
 */
const getAnalyseTitleAmount = computed(() => {
    let title = ""
    if (emConsoCompareStore.compareChoice === "folder") {
        title = t(
            "analyse_folder",
            {
                total: emConsoCompareStore.compareListIdByFolder.length,
            },
            emConsoCompareStore.compareListIdByFolder.length
        )
    } else {
        title = t(
            "analyse_building",
            {
                total: emConsoCompareStore.compareListId.length,
            },
            emConsoCompareStore.compareListId.length
        )
    }

    return (
        title +
        " " +
        t(
            "source",
            {
                total: emConsoCompareStore.getSourcesCount,
            },
            emConsoCompareStore.getSourcesCount
        )
    )
})

/**
 * Tooltip listant dossiers ou bâtiments comparés (si peu nombreux).
 */
const getTooltipTitle = computed(() => {
    if (
        emConsoCompareStore.compareChoice === "folder" &&
        emConsoCompareStore.compareListIdByFolder.length < 4
    ) {
        return t("analyse_folder_tooltip")
    } else if (
        emConsoCompareStore.compareChoice === "building" &&
        emConsoCompareStore.compareListId.length < 4
    ) {
        return t("analyse_building_tooltip")
    }
    return null
})

/** Export référentiel technique activé pour l’entreprise (beta). */
const getShowExportReferentiel = computed(() => {
    return authStore.getBetaFeatures?.export_referentiel === true
})
</script>
<template>
    <div class="dashboard-header flex flex-col gap-14">
        <HeaderPage
            :title="t('title', { firstname: authStore.firstname })"
            :breadcrumb="breadcrumb"
            class-header="dashboard-header__page-header flex flex-wrap items-start justify-between gap-4"
        >
            <template #subtitle>
                <p class="text-sm text-primary" v-html="t('sub_title')"></p>
            </template>
            <div class="flex shrink-0 flex-wrap gap-2">
                <BaseButton
                    :label="t('export')"
                    icon="pi pi-download"
                    @click="visibleModalExport = true"
                ></BaseButton>
                <BaseButton
                    v-if="getShowExportReferentiel"
                    :label="t('export_rt')"
                    icon="pi pi-download"
                    @click="visibleModalExportRt = true"
                ></BaseButton>
            </div>
        </HeaderPage>

        <DashboardHeaderMessage
            v-if="showHelperMessage && isHelperMessageVisible"
            @close="isHelperMessageVisible = false"
        />
        <div
            class="dashboard-header__analyse flex flex-wrap items-center justify-center gap-3"
        >
            <h2 class="text-2xl">
                {{ t("analyse_title") }}
            </h2>
            <p
                v-tooltip.bottom="getTooltipTitle"
                class="text-2xl font-semibold"
            >
                {{ getAnalyseTitleAmount }}
            </p>
            <BaseTooltipMessage
                v-if="emConsoCompareStore.getDisplayDataConfig?.messageLimit"
                :title="t('max_source_warning.title')"
                :content="t('max_source_warning.content')"
                severity="warn"
            />
            <BaseButton
                :label="t('modify')"
                outlined
                @click="visible = true"
            ></BaseButton>
        </div>
        <DashboardModalSelectBuilding
            v-model:visible="visible"
        ></DashboardModalSelectBuilding>
        <DashboardExportDialog v-model:visible="visibleModalExport" />
        <DashboardExportRTDialog v-model:visible="visibleModalExportRt" />
    </div>
</template>
